import { API_URL, getApiErrorMessage } from '../api'

export type HealEvent = {
  id: number; event_type: string; status: string; actor: string; message: string
  details: Record<string, unknown> | null; created_at: string
}

export type HealRequest = {
  id: string; sequence_number: number; source: string; status: string; requested_by: string
  description: string; expected_actual: string | null; reproduction_steps: string | null
  environment: string; severity: string; correlation_id: string | null; failing_url: string | null
  occurrence_count: number; attachment_name: string | null; diagnosis: string | null
  remediation: string | null; evidence: Record<string, unknown> | null
  recommended_provider: string | null; recommended_model: string | null; model_reason: string | null
  branch_name: string | null; base_branch: string | null; commit_sha: string | null
  pull_request_id: string | null; pull_request_url: string | null
  pipeline_run_id: string | null; pipeline_url: string | null
  files_changed: string[]; test_results: Record<string, unknown> | null; failure_reason: string | null
  created_at: string; updated_at: string; completed_at: string | null; events: HealEvent[]
}

export type HealModelConfiguration = {
  provider: 'azure_openai' | 'cloudflare' | 'local_cli'; model: string
  updated_by: string | null; updated_at: string | null
}

export type HealProvider = {
  name: string; provider_id: string; configured: boolean
  models: { id: string; label: string }[]; models_source?: 'live' | 'configured' | null
}

async function checked<T>(response: Response): Promise<T> {
  if (!response.ok) throw new Error(await getApiErrorMessage(response))
  return response.json() as Promise<T>
}

export type HealAccess = { allowed: boolean; username: string; local_mode: boolean }

export async function verifyHealAccess(): Promise<HealAccess> {
  return checked(await fetch(`${API_URL}/heal/access`))
}

export async function listHealRequests(): Promise<HealRequest[]> {
  return checked(await fetch(`${API_URL}/heal?limit=200`))
}

export async function markHealRequestsFixed(ids: string[]): Promise<HealRequest[]> {
  return checked(await fetch(`${API_URL}/heal/mark-fixed`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ request_ids: ids }),
  }))
}

export async function executeFixPackageLocally(
  id: string, file: File, devBranchUrl: string, qaBranch: string,
): Promise<HealRequest> {
  const form = new FormData()
  form.set('package', file)
  form.set('dev_branch_url', devBranchUrl)
  form.set('qa_branch', qaBranch)
  return checked(await fetch(`${API_URL}/heal/${encodeURIComponent(id)}/execute-fix-package`, {
    method: 'POST', body: form,
  }))
}

export async function submitHealRequest(form: FormData): Promise<HealRequest> {
  return checked(await fetch(`${API_URL}/heal`, { method: 'POST', body: form }))
}

export async function analyzeHealRequest(id: string): Promise<HealRequest> {
  return checked(await fetch(`${API_URL}/heal/${encodeURIComponent(id)}/analyze`, { method: 'POST' }))
}

export async function getHealModelConfiguration(): Promise<HealModelConfiguration> {
  return checked(await fetch(`${API_URL}/heal/model-config`))
}

export async function getHealModelOptions(refresh = false): Promise<HealProvider[]> {
  const result = await checked<{ providers: HealProvider[] }>(await fetch(`${API_URL}/heal/model-options${refresh ? '?refresh=true' : ''}`))
  return result.providers
}

export async function saveHealModelConfiguration(config: Pick<HealModelConfiguration, 'provider' | 'model'>): Promise<HealModelConfiguration> {
  return checked(await fetch(`${API_URL}/heal/model-config`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(config),
  }))
}

export async function downloadHealFix(id: string, sequenceNumber: number): Promise<void> {
  const response = await fetch(`${API_URL}/heal/${encodeURIComponent(id)}/fix-package`)
  if (!response.ok) throw new Error(await getApiErrorMessage(response))
  const url = URL.createObjectURL(await response.blob())
  const anchor = document.createElement('a')
  anchor.href = url; anchor.download = `HEAL-${String(sequenceNumber).padStart(5, '0')}-fix.zip`
  anchor.click(); URL.revokeObjectURL(url)
}
